<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink, useRoute } from 'vue-router'

import AppIcon from '@/components/common/AppIcon.vue'
import { useMenuAccordion } from '@/composables/useMenuAccordion'
import { menuLabelToKey, translateMenuLabel } from '@/i18n/menuLabel'
import { normalizeItemPath, normalizeMenuPath } from '@/stores/menu'
import { useUserStore } from '@/stores/user'
import type { MenuItem } from '@/types/menu'
import { defaultMenuIcon, sectionColor } from '@/utils/menuAppearance'

const props = withDefaults(
  defineProps<{
    item: MenuItem
    /** Nesting level, used only for the left indentation. */
    depth?: number
    collapsed?: boolean
    /** Colour class of the section this entry belongs to, set by the parent. */
    accent?: string | null
    /** Last of its siblings: its tree branch closes with a corner (└). */
    last?: boolean
    /**
     * Open state, owned by the parent so only one sibling is open at a time
     * (see `useMenuAccordion`). `null` = nobody owns it and the entry keeps its
     * own state, which is how it behaves when mounted on its own.
     */
    open?: boolean | null
  }>(),
  { depth: 0, collapsed: false, accent: null, last: false, open: null },
)

const emit = defineEmits<{ toggle: [] }>()

const route = useRoute()
const { t } = useI18n()

const user = useUserStore()

/**
 * The "Gruppi" category is named after the group selected in the header, so
 * it renames itself on every group switch. With no group selected it keeps
 * its usual translated label.
 */
const groupName = computed(() => user.selectedGroup?.name?.trim() || null)

/** The "Gruppi" category while it carries the selected group's name. */
const isGroupEntry = computed(
  () => groupName.value !== null && menuLabelToKey(props.item.label) === 'gruppi',
)

const label = computed(() =>
  isGroupEntry.value ? groupName.value! : translateMenuLabel(props.item.label),
)
const children = computed(() => props.item.children ?? [])

/**
 * Every section — an entry with children, at any depth — has a colour of its
 * own and hands it down to its plain entries: a glance tells which area a page
 * belongs to. A nested section steers clear of its parent's colour, so a
 * sub-menu inside "Gruppi" doesn't blend into it. Plain entries just inherit.
 */
const color = computed(() => {
  if (props.accent && children.value.length === 0) return props.accent
  return sectionColor(props.item.label, props.accent)
})

/** The backend icon always wins; the default only fills an empty one. */
/**
 * Row style: the indentation, plus — for the group entry — the section colour
 * the `.menu-group-entry` rule tints the row with (see the style block).
 */
const rowStyle = computed(() => {
  if (!isGroupEntry.value) return indent.value
  return { ...indent.value, '--entry-color': `var(${color.value.replace(/^text-/, '--color-')})` }
})

const icon = computed(() => props.item.icon?.trim() || defaultMenuIcon(props.item.label))

/**
 * An entry with children behaves as a category: clicking it expands/collapses
 * instead of navigating.
 */
const isGroup = computed(() => children.value.length > 0)

/** Sum of the pending counts anywhere below this entry, at any depth. */
const nestedCount = computed(() => {
  const sum = (items: MenuItem[]): number =>
    items.reduce((total, item) => total + (item.pending_count ?? 0) + sum(item.children ?? []), 0)
  return sum(children.value)
})

/**
 * The number next to the label. A closed category adds up everything waiting
 * inside it, or there would be no way to tell from the closed menu that
 * something needs a look; once open, the entries show their own numbers and the
 * total would only count them twice.
 */
const badgeCount = computed(
  () => (props.item.pending_count ?? 0) + (expanded.value ? 0 : nestedCount.value),
)

/** On the icon rail no badge fits and no child shows: a dot stands for the lot. */
const railCount = computed(() => (props.item.pending_count ?? 0) + nestedCount.value)

/** Rail tooltip: the label, with the count when there is one. */
const railTitle = computed(() =>
  railCount.value > 0 ? `${label.value} (${railCount.value})` : label.value,
)

/** `path` is null for categories and for pages not wired up yet. */
const itemPath = computed(() => normalizeItemPath(props.item.path))

const containsActiveRoute = computed(() => {
  const current = normalizeMenuPath(route.path)
  const walk = (items: MenuItem[]): boolean =>
    items.some((item) => normalizeItemPath(item.path) === current || walk(item.children ?? []))
  return walk(children.value)
})

// Without a parent owning it: start open when the active page lives inside.
const ownOpen = ref(containsActiveRoute.value)
const expanded = computed(() => props.open ?? ownOpen.value)

function toggle(): void {
  if (props.open === null) ownOpen.value = !ownOpen.value
  emit('toggle')
}

// This entry's children: one of them open at a time.
const accordion = useMenuAccordion(children, () => route.path)

const isActive = computed(
  () => itemPath.value !== null && itemPath.value === normalizeMenuPath(route.path),
)

/** Left padding of a row and extra indentation per level, in rem. */
const ROW_PADDING = 0.75
const LEVEL_STEP = 1.5
/** Half the width of a `text-lg` icon: where the tree line runs under it. */
const ICON_HALF = 0.5625

const indent = computed(() =>
  props.collapsed ? undefined : { paddingLeft: `${ROW_PADDING + props.depth * LEVEL_STEP}rem` },
)

/**
 * Tree lines of a sub-menu entry: a vertical line dropping from under the
 * parent's icon, and a branch from it to this entry. Drawn by the `<li>`
 * (see the style block) from these two variables — where the line runs and
 * which colour it takes, the parent section's, so a branch reads as part of it.
 *
 * Tailwind v4 exposes every colour it generates as a CSS variable, so the
 * `text-red-400` class of the section maps to `var(--color-red-400)`.
 */
const treeVars = computed(() => {
  if (props.collapsed || props.depth === 0) return undefined
  const lineX = ROW_PADDING + (props.depth - 1) * LEVEL_STEP + ICON_HALF
  const branchTo = ROW_PADDING + props.depth * LEVEL_STEP - 0.25
  return {
    '--tree-x': `${lineX}rem`,
    '--tree-branch': `${branchTo - lineX}rem`,
    '--tree-color': props.accent ? `var(${props.accent.replace(/^text-/, '--color-')})` : undefined,
  }
})
</script>

<template>
  <li :class="{ 'menu-tree-node': treeVars, 'menu-tree-last': treeVars && last }" :style="treeVars">
    <!-- Category: toggles its children -->
    <button
      v-if="isGroup"
      type="button"
      class="chrome-item w-full cursor-pointer justify-between"
      :class="{
        'chrome-item-active': containsActiveRoute && !expanded,
        'menu-group-entry': isGroupEntry,
      }"
      :style="rowStyle"
      :aria-expanded="expanded"
      :title="collapsed ? railTitle : undefined"
      @click="toggle"
    >
      <span class="flex min-w-0 items-center gap-3">
        <span class="relative inline-flex">
          <AppIcon :name="icon" fallback="mdi-folder-outline" class="text-lg" :class="color" />
          <span v-if="collapsed && railCount > 0" class="menu-rail-dot" aria-hidden="true" />
        </span>
        <span v-if="!collapsed" class="flex min-w-0 items-center gap-1.5">
          <span class="truncate">{{ label }}</span>
          <span v-if="item.is_new" class="badge bg-gold-500 text-abyss-900 shrink-0 uppercase">{{
            t('common.newBadge')
          }}</span>
          <span v-if="badgeCount" class="badge bg-primary-600 shrink-0 text-white">
            {{ badgeCount > 99 ? '99+' : badgeCount }}
            <span v-if="!expanded && nestedCount" class="sr-only">
              {{ t('sidebar.pendingInside', { count: nestedCount }) }}
            </span>
          </span>
        </span>
      </span>
      <AppIcon
        v-if="!collapsed"
        :name="expanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
        class="text-base opacity-70"
      />
    </button>

    <!--
      Leaf with a real page. Naviga con `itemPath`, NON con `item.path` grezzo:
      a database il path può arrivare senza slash iniziale (`rank/alliances`
      accanto a `/news`), e vue-router lo tratterebbe come **relativo** alla
      rotta corrente. Stando su /rank/alliances, un altro click sulla stessa
      voce portava a /rank/rank/alliances, poi /rank/rank/rank/alliances, e da
      lì al 404. `itemPath` è la forma canonica già usata dal confronto per lo
      stato attivo: link ed evidenziazione non possono più divergere.
    -->
    <RouterLink v-else-if="itemPath" :to="itemPath" custom>
      <template #default="{ navigate, href }">
        <a
          :href="href"
          class="chrome-item"
          :class="{ 'chrome-item-active': isActive, 'menu-group-entry': isGroupEntry }"
          :aria-current="isActive ? 'page' : undefined"
          :style="rowStyle"
          :title="collapsed ? railTitle : undefined"
          @click="navigate"
        >
          <span class="relative inline-flex">
            <AppIcon :name="icon" fallback="mdi-chevron-right" class="text-lg" :class="color" />
            <span v-if="collapsed && railCount > 0" class="menu-rail-dot" aria-hidden="true" />
          </span>
          <span v-if="!collapsed" class="flex min-w-0 items-center gap-1.5">
            <span class="truncate">{{ label }}</span>
            <span v-if="item.is_new" class="badge bg-gold-500 text-abyss-900 shrink-0 uppercase">{{
              t('common.newBadge')
            }}</span>
            <span v-if="item.pending_count" class="badge bg-primary-600 shrink-0 text-white">{{
              item.pending_count > 99 ? '99+' : item.pending_count
            }}</span>
          </span>
        </a>
      </template>
    </RouterLink>

    <!-- Leaf without a path: the backend declared the entry but no page yet.
         Shown, but not pretending to be a link. -->
    <span
      v-else
      class="chrome-item cursor-default opacity-55"
      :class="{ 'menu-group-entry': isGroupEntry }"
      :style="rowStyle"
      :title="collapsed ? railTitle : undefined"
      aria-disabled="true"
    >
      <span class="relative inline-flex">
        <AppIcon :name="icon" fallback="mdi-circle-small" class="text-lg" :class="color" />
        <span v-if="collapsed && railCount > 0" class="menu-rail-dot" aria-hidden="true" />
      </span>
      <span v-if="!collapsed" class="flex min-w-0 items-center gap-1.5">
        <span class="truncate">{{ label }}</span>
        <span v-if="item.is_new" class="badge bg-gold-500 text-abyss-900 shrink-0 uppercase">{{
          t('common.newBadge')
        }}</span>
        <span v-if="item.pending_count" class="badge bg-primary-600 shrink-0 text-white">{{
          item.pending_count > 99 ? '99+' : item.pending_count
        }}</span>
      </span>
    </span>

    <ul v-if="isGroup && expanded && !collapsed" class="mt-0.5 space-y-0.5">
      <SidebarMenuItem
        v-for="(child, index) in children"
        :key="child.id"
        :item="child"
        :depth="depth + 1"
        :collapsed="collapsed"
        :accent="color"
        :last="index === children.length - 1"
        :open="accordion.isOpen(child.id)"
        @toggle="accordion.toggle(child.id)"
      />
    </ul>
  </li>
</template>

<style scoped>
/*
 * Tree lines of the sub-menus. Plain CSS rather than utilities because they
 * are pseudo-elements fed by the variables of `treeVars`.
 *
 * The geometry follows the rows: `--tree-row-mid` is half the height of a
 * `.chrome-item` row (py-2 around a `text-lg` icon = 45px), `--tree-gap` the
 * `space-y-0.5` between rows — the line has to bridge it or it would come out
 * dashed. Change the row padding or the icon size and these two move with it.
 */
.menu-tree-node {
  --tree-row-mid: 1.40625rem;
  --tree-gap: 0.125rem;
  --tree-line: color-mix(in oklab, var(--tree-color, var(--color-parchment-200)) 50%, transparent);
  position: relative;
}

/* Vertical line: through this row and its children, down to the next sibling. */
.menu-tree-node::before {
  content: '';
  position: absolute;
  left: var(--tree-x);
  top: calc(-1 * var(--tree-gap));
  bottom: 0;
  border-left: 1.5px solid var(--tree-line);
  pointer-events: none;
}

/* The first child reaches up under the parent's icon, past its bottom padding. */
.menu-tree-node:first-child::before {
  top: calc(-1 * var(--tree-gap) - 0.5rem);
}

/* Branch towards this entry's icon. */
.menu-tree-node::after {
  content: '';
  position: absolute;
  left: var(--tree-x);
  top: var(--tree-row-mid);
  width: var(--tree-branch);
  border-top: 1.5px solid var(--tree-line);
  pointer-events: none;
}

/*
 * Last sibling: the line stops at its row and turns into the branch with a
 * rounded corner (└) instead of carrying on past it.
 */
.menu-tree-last::before {
  bottom: auto;
  height: calc(var(--tree-row-mid) + var(--tree-gap));
  width: var(--tree-branch);
  border-bottom: 1.5px solid var(--tree-line);
  border-bottom-left-radius: 0.375rem;
}

.menu-tree-last:first-child::before {
  height: calc(var(--tree-row-mid) + var(--tree-gap) + 0.5rem);
}

.menu-tree-last::after {
  content: none;
}

/*
 * The entry named after the selected group: it says which group every page
 * below it is about, so it stands out from the plain categories — bold, white,
 * on a tint of its own section colour with a hairline of the same hue. Plain
 * CSS and unlayered, so it wins over `.chrome-item`'s hover background.
 */
.menu-group-entry {
  color: #fff;
  font-weight: 600;
  background-color: color-mix(in oklab, var(--entry-color) 16%, transparent);
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--entry-color) 40%, transparent);
}

.menu-group-entry:hover {
  background-color: color-mix(in oklab, var(--entry-color) 26%, transparent);
}

/*
 * Count marker on the collapsed icon rail, where badges don't fit: a red dot on
 * the icon's corner, ringed with the sidebar colour so it reads on any icon.
 */
.menu-rail-dot {
  position: absolute;
  top: -0.125rem;
  right: -0.25rem;
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 9999px;
  background-color: var(--color-red-500);
  box-shadow: 0 0 0 2px var(--color-abyss-900);
}
</style>
